import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { DecisionBreakdown, DecisionBreakdownBy } from "./use-decisions.ts";
import {
  quantityMeasure,
  isFailClosedRow,
  eventsHref,
  BREAKDOWN_DIMENSIONS,
  type BreakdownDimension,
} from "./vocab.ts";

/**
 * The breakdown table with its `by` dimension control (task 11.6; design 12.2 "breakdown by reason,
 * tier, operation, gateway"; 12.3 drill-down; 12.4 semantic table + keyboard-operable control).
 *
 * The `by` control is a keyboard-operable segmented button group (reason / tier / operation /
 * gateway). Selecting one calls `onByChange`, which re-queries `GET /decisions/breakdown?by=…` in
 * `use-decisions.ts` — the breakdown is keyed on `by`, so each dimension's rows are fetched and
 * cached independently and flipping back is instant. The control reflects the ACTIVE dimension with
 * `aria-pressed`, so its state is legible to assistive tech, not conveyed by colour alone.
 *
 * Each row is rendered through the shared semantic `DenseTable`: the key (reason/tier/operation/
 * gateway), allow, deny and total — each a `Quantity` labelled with its confidence — and the drill.
 * Every row drills to the events behind it using the row's server-provided `drill` {@link EventQuery}
 * (the authoritative filter for exactly those events). A fail-closed row (its contract `highlight`)
 * is marked prominently with a warning `StatusBadge` (icon + text, never colour alone) so an
 * operator spots fail-closed concentrations in whichever dimension they are breaking down by.
 *
 * `unexpected_reason_count` (reasons outside the catalog, mapped to `other`; FR-3.2) is surfaced as
 * an honest note above the table so a reader knows some rows were folded into `other`.
 */

type Row = DecisionBreakdown["rows"][number];

export interface BreakdownTableProps {
  readonly by: DecisionBreakdownBy;
  readonly onByChange: (by: DecisionBreakdownBy) => void;
  /** The breakdown for the current `by`, or undefined while the first fetch is in flight. */
  readonly breakdown: DecisionBreakdown | undefined;
  /** True while a (re)fetch for the current `by` is in flight. */
  readonly loading: boolean;
}

export function BreakdownTable(props: BreakdownTableProps): ReactNode {
  const navigate = useNavigate();
  // BREAKDOWN_DIMENSIONS is a non-empty static list, so [0] is always present (the `!` satisfies
  // noUncheckedIndexedAccess); `active` is therefore always a BreakdownDimension, never undefined.
  const active = BREAKDOWN_DIMENSIONS.find((d) => d.by === props.by) ?? BREAKDOWN_DIMENSIONS[0]!;

  const rows = props.breakdown?.rows ?? [];
  // Busiest first (by total), so the heaviest keys are at the top; a null total sorts last.
  const sorted = [...rows].sort((a, b) => total(b) - total(a));

  const columns: readonly Column<Row>[] = [
    {
      key: "key",
      header: active.keyHeader,
      isRowHeader: true,
      cell: (r) => (
        <span className="dec-breakdown__key">
          {isFailClosedRow(r.highlight) && (
            <StatusBadge
              level="warn"
              label="Fail-closed"
              size="sm"
              title={`${r.key} is a fail-closed outcome. The boundary could not reach or verify a decision.`}
            />
          )}
          <button
            type="button"
            className="dec-link"
            onClick={() => navigate(eventsHref(r.drill))}
            aria-label={`Open the events behind ${active.label.toLowerCase()} ${r.key}`}
          >
            <code>{r.key}</code>
          </button>
        </span>
      ),
    },
    quantityColumn("allow", "Allow", active),
    quantityColumn("deny", "Deny", active),
    quantityColumn("total", "Total", active),
  ];

  return (
    <section className="dec-breakdown" aria-label="Decision breakdown">
      <div className="dec-section__head">
        <h2 className="dec-section__title">Breakdown</h2>
        <ByControl by={props.by} onByChange={props.onByChange} />
      </div>

      {props.breakdown !== undefined && props.breakdown.unexpected_reason_count > 0 && (
        <p className="dec-breakdown__note">
          {defaultNumberFormat(props.breakdown.unexpected_reason_count)} decision(s) had a reason
          outside the catalog and were counted under <code>other</code>.
        </p>
      )}

      <DenseTable
        caption={`Decisions broken down by ${active.label.toLowerCase()}`}
        columns={columns}
        rows={sorted}
        rowKey={(r) => r.key}
        stickyHeader
        emptyMessage={
          props.loading
            ? "Loading the breakdown…"
            : `No decisions to break down by ${active.label.toLowerCase()} in this window.`
        }
      />
    </section>
  );
}

/** The keyboard-operable segmented control that chooses the `by` dimension (re-queries on change). */
function ByControl(props: {
  by: DecisionBreakdownBy;
  onByChange: (by: DecisionBreakdownBy) => void;
}): ReactNode {
  return (
    <div className="dec-by" role="group" aria-label="Break down by">
      {BREAKDOWN_DIMENSIONS.map((d) => {
        const pressed = d.by === props.by;
        return (
          <button
            key={d.by}
            type="button"
            className="dec-by__btn"
            aria-pressed={pressed}
            onClick={() => props.onByChange(d.by)}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

/** A right-aligned `Quantity` column (allow/deny/total) with its exact/estimate confidence. */
function quantityColumn(
  key: "allow" | "deny" | "total",
  header: string,
  active: BreakdownDimension,
): Column<Row> {
  return {
    key,
    header,
    align: "right",
    cell: (r) => {
      const q = r[key];
      const measure = quantityMeasure(q);
      const text = q.value === null ? "n/a" : `${defaultNumberFormat(q.value)}${estimateSuffix(measure)}`;
      return (
        <span className="dec-num" data-measure={measure} title={`${header} for ${active.label.toLowerCase()} ${r.key}`}>
          {text}
        </span>
      );
    },
  };
}

/** The numeric total for sorting; a null total sorts to the bottom. */
function total(r: Row): number {
  const v = r.total.value;
  return v === null ? -1 : v;
}
